// Discord-style message formatting, shared by the web app (drawing it) and the room (what counts as a mention, which
// links get previews, plain text for notifications and reply excerpts). One parser, so both sides agree on what is
// code, a spoiler or a link.
//
// Inline:  **bold**  *italic* or _italic_  __underline__  ~~strike~~  ||spoiler||  `code`  ``code with ` in it``
//          <t:1700000000:R> timestamps  <:name:id> custom emoji  \* escapes
// Blocks:  ```lang code blocks```  > quote line  >>> quote to the end  # / ## / ### headings  -# subtext
//          - bullet and 1. numbered lists (indent to nest)
//
// Like Discord, newlines are kept as typed (no paragraph merging), headings need "# " at the start of a line, "_" only
// italicises between word boundaries (snake_case stays), and links, @mentions and #channels inside code are just text.
// Text leaves are plain strings: the app draws @mentions, #channels and bare links in them as it always has.
import { splitLinks } from "./links.ts";

export type TimeStyle = "t" | "T" | "d" | "D" | "f" | "F" | "R";

export type MdNode =
  | { t: "text"; v: string }
  | { t: "strong" | "em" | "u" | "s" | "spoiler"; c: MdNode[] }
  | { t: "code"; v: string }
  | { t: "codeblock"; lang: string | null; v: string }
  | { t: "quote"; c: MdNode[] }
  | { t: "heading"; level: 1 | 2 | 3; c: MdNode[] }
  | { t: "subtext"; c: MdNode[] }
  | { t: "list"; ordered: boolean; start: number; items: MdNode[][] }
  | { t: "time"; ts: number; style: TimeStyle }
  /** A server's custom emoji, "<:name:id>" ("<a:…>" animated); see emoji.ts. */
  | { t: "emoji"; name: string; id: string; animated: boolean };

export interface ParseOptions {
  /** A message still being written (@Bloom streaming): an unclosed ``` runs to the end as a code block. */
  open?: boolean;
}

interface State {
  /** Inside inline formatting: no block rules (headings, lists, quotes). */
  inline: boolean;
  /** Inside a quote: quotes don't nest. */
  quote: boolean;
  open: boolean;
}

// Every rule is sticky (y): it's tried at an exact position of the whole text, so lookbehinds and \b see what came
// before (that's how "snake_case" keeps its underscores).
const R = {
  escape: /\\([^0-9A-Za-z\s])/y,
  codeblock: /```(?:([A-Za-z0-9_+#.-]{1,32})\n)?\n*([\s\S]*?)\n*```/y,
  codeblockOpen: /```(?:([A-Za-z0-9_+#.-]{1,32})\n)?\n*([\s\S]*)$/y,
  code: /(`+)([\s\S]*?[^`])\1(?!`)/y,
  angleLink: /<(?:https?:\/\/|www\.)[^\s<>]+>/y,
  token: /<[@#][0-9A-Za-z_-]{1,64}>/y,
  emoji: /<(a?):([A-Za-z0-9_]{2,32}):([0-9A-Za-z]{1,32})>/y,
  time: /<t:(-?\d{1,13})(?::([tTdDfFR]))?>/y,
  url: /(?:https?:\/\/|www\.)[^\s<\u200E\u200F\u202A-\u202E\u2066-\u2069]+[^<.,:;"'!?*_~|)\]\s\u200E\u200F\u202A-\u202E\u2066-\u2069]/y,
  spoiler: /\|\|([\s\S]+?)\|\|/y,
  strong: /\*\*([\s\S]+?)\*\*(?!\*)/y,
  underline: /__([\s\S]+?)__(?!_)/y,
  emStar: /\*(?=\S)((?:\*\*|\\[\s\S]|\s+(?:\\[\s\S]|[^\s*\\]|\*\*)|[^\s*\\])+?)\*(?!\*)/y,
  emUnder: /\b_((?:__|\\[\s\S]|[^\\_])+?)_\b/y,
  strike: /~~([\s\S]+?)~~/y,
  // Blocks (only at the start of a line).
  quoteRest: />>> ([\s\S]*)/y,
  quoteLine: /> ([^\n]*)(?:\n|$)/y,
  heading: /(#{1,3}) +(?=\S)([^\n]*?)[ \t]*(?:\n|$)/y,
  subtext: /-# +(?=\S)([^\n]*?)[ \t]*(?:\n|$)/y,
  listItem: /( *)([-*]|\d{1,9}\.) +(?=\S)([^\n]*)(?:\n|$)/y,
};

function at(re: RegExp, src: string, i: number): RegExpExecArray | null {
  re.lastIndex = i;
  return re.exec(src);
}

/** Characters that can start an inline rule; anything else is plain text. */
const TRIGGER = new Set(["\\", "`", "<", "|", "*", "_", "~", "h", "w"]);

/** "` a `" → "a" (one padding space each side, as CommonMark and Discord do). */
const codeText = (v: string) => (v.length > 2 && v.startsWith(" ") && v.endsWith(" ") && v.trim() ? v.slice(1, -1) : v);

function parseInto(src: string, st: State): MdNode[] {
  const out: MdNode[] = [];
  let buf = "";
  const flush = () => {
    if (buf) out.push({ t: "text", v: buf });
    buf = "";
  };
  /** A block starts: the newline that ended the text before it is the block's own edge, not an extra line. */
  const block = (n: MdNode) => {
    if (buf.endsWith("\n")) buf = buf.slice(0, -1);
    flush();
    out.push(n);
  };
  const inner = (s: string, extra: Partial<State> = {}) => parseInto(s, { ...st, inline: true, ...extra });

  let i = 0;
  while (i < src.length) {
    const ch = src[i]!;
    const lineStart = i === 0 || src[i - 1] === "\n";
    let m: RegExpExecArray | null;

    if (lineStart && !st.inline) {
      if (!st.quote && (m = at(R.quoteRest, src, i))) {
        block({ t: "quote", c: parseInto(m[1]!, { ...st, quote: true }) });
        i = src.length;
        continue;
      }
      if (!st.quote && (m = at(R.quoteLine, src, i))) {
        // Consecutive "> " lines are one quote.
        const lines = [m[1]!];
        let j = i + m[0].length;
        while (j < src.length && (m = at(R.quoteLine, src, j))) {
          lines.push(m[1]!);
          j += m[0].length;
        }
        block({ t: "quote", c: parseInto(lines.join("\n"), { ...st, quote: true }) });
        i = j;
        continue;
      }
      if ((m = at(R.heading, src, i))) {
        block({ t: "heading", level: m[1]!.length as 1 | 2 | 3, c: inner(m[2]!) });
        i += m[0].length;
        continue;
      }
      if ((m = at(R.subtext, src, i))) {
        block({ t: "subtext", c: inner(m[1]!) });
        i += m[0].length;
        continue;
      }
      if ((m = at(R.listItem, src, i))) {
        const items: { indent: number; ordered: boolean; n: number; text: string }[] = [];
        let j = i;
        while (j < src.length && (m = at(R.listItem, src, j))) {
          const ordered = /\d/.test(m[2]![0]!);
          // Switching between bullets and numbers at the top level starts a new list.
          if (items.length && m[1]!.length <= items[0]!.indent && ordered !== items[0]!.ordered) break;
          items.push({ indent: m[1]!.length, ordered, n: ordered ? Number(m[2]!.slice(0, -1)) : 0, text: m[3]! });
          j += m[0].length;
        }
        block(buildList(items, 0, items.length, inner));
        i = j;
        continue;
      }
    }

    if (!TRIGGER.has(ch)) {
      buf += ch;
      i++;
      continue;
    }
    if ((m = at(R.escape, src, i))) {
      buf += m[1];
      i += m[0].length;
    } else if ((m = at(R.codeblock, src, i)) || (st.open && (m = at(R.codeblockOpen, src, i)))) {
      block({ t: "codeblock", lang: m[1]?.toLowerCase() ?? null, v: m[2]! });
      i += m[0].length;
      if (src[i] === "\n") i++; // the line break after the fence belongs to the block
    } else if ((m = at(R.code, src, i))) {
      flush();
      out.push({ t: "code", v: codeText(m[2]!) });
      i += m[0].length;
    } else if ((m = at(R.time, src, i))) {
      flush();
      out.push({ t: "time", ts: Number(m[1]) * 1000, style: (m[2] as TimeStyle | undefined) ?? "f" });
      i += m[0].length;
    } else if ((m = at(R.emoji, src, i))) {
      flush();
      out.push({ t: "emoji", animated: m[1] === "a", name: m[2]!, id: m[3]! });
      i += m[0].length;
    } else if ((m = at(R.angleLink, src, i)) || (m = at(R.token, src, i)) || (m = at(R.url, src, i))) {
      // Links and stored tokens are atomic text: no "_" or "*" inside them starts formatting.
      buf += m[0];
      i += m[0].length;
    } else if ((m = at(R.spoiler, src, i))) {
      flush();
      out.push({ t: "spoiler", c: inner(m[1]!) });
      i += m[0].length;
    } else if ((m = at(R.strong, src, i))) {
      flush();
      out.push({ t: "strong", c: inner(m[1]!) });
      i += m[0].length;
    } else if ((m = at(R.underline, src, i))) {
      flush();
      out.push({ t: "u", c: inner(m[1]!) });
      i += m[0].length;
    } else if ((m = at(R.emStar, src, i)) || (m = at(R.emUnder, src, i))) {
      flush();
      out.push({ t: "em", c: inner(m[1]!) });
      i += m[0].length;
    } else if ((m = at(R.strike, src, i))) {
      flush();
      out.push({ t: "s", c: inner(m[1]!) });
      i += m[0].length;
    } else {
      buf += ch;
      i++;
    }
  }
  flush();
  return out;
}

/** Nests list items by indent: an item indented past the one before it starts a list inside that item. */
function buildList(
  items: { indent: number; ordered: boolean; n: number; text: string }[],
  from: number,
  to: number,
  inner: (s: string) => MdNode[],
): MdNode {
  const first = items[from]!;
  const list: Extract<MdNode, { t: "list" }> = { t: "list", ordered: first.ordered, start: first.n || 1, items: [] };
  let k = from;
  while (k < to) {
    const item = items[k]!;
    let end = k + 1;
    while (end < to && items[end]!.indent > item.indent) end++;
    const content = inner(item.text);
    if (end > k + 1) content.push(buildList(items, k + 1, end, inner));
    list.items.push(content);
    k = end;
  }
  return list;
}

const cache = new Map<string, MdNode[]>();

/** Parses message text (cached: the same message is drawn many times). */
export function parseMarkdown(src: string, opts: ParseOptions = {}): MdNode[] {
  const key = opts.open ? `\u0001${src}` : src;
  const hit = cache.get(key);
  if (hit) return hit;
  const nodes = parseInto(src.replace(/\r\n?/g, "\n"), { inline: false, quote: false, open: !!opts.open });
  if (cache.size > 2000) cache.clear();
  cache.set(key, nodes);
  return nodes;
}

/** True when the text has anything for the formatter to do (most messages don't: they skip it entirely). */
export const hasMarkdown = (src: string) => /[\\`*_~|<]|^\s*(?:>|#|-#|[-*] |\d+\. )/m.test(src);

// ---- Source view, for the composer ----

/**
 * The message as typed, every character kept (the marks too), grouped by what it does: the composer draws this behind
 * its text box so formatting shows while you type, like Discord. Joining every leaf gives back the exact text.
 */
export type SrcNode =
  | { t: "text"; v: string }
  /** Formatting marks: "**", "||", "> ", "# ", "\\"… */
  | { t: "mark"; v: string }
  | { t: "strong" | "em" | "u" | "s" | "spoiler" | "code" | "codeblock" | "quote" | "subtext" | "li" | "time"; c: SrcNode[] }
  | { t: "heading"; level: 1 | 2 | 3; c: SrcNode[] };

function sourceInto(src: string, st: State): SrcNode[] {
  const out: SrcNode[] = [];
  let buf = "";
  const flush = () => {
    if (buf) out.push({ t: "text", v: buf });
    buf = "";
  };
  const push = (n: SrcNode) => {
    flush();
    out.push(n);
  };
  const mark = (v: string): SrcNode[] => (v ? [{ t: "mark", v }] : []);
  const text = (v: string): SrcNode[] => (v ? [{ t: "text", v }] : []);
  const inner = (s: string, extra: Partial<State> = {}) => sourceInto(s, { ...st, inline: true, ...extra });

  let i = 0;
  while (i < src.length) {
    const ch = src[i]!;
    const lineStart = i === 0 || src[i - 1] === "\n";
    let m: RegExpExecArray | null;

    if (lineStart && !st.inline) {
      if (!st.quote && (m = at(R.quoteRest, src, i))) {
        push({ t: "quote", c: [...mark(">>> "), ...sourceInto(m[1]!, { ...st, quote: true })] });
        i = src.length;
        continue;
      }
      if (!st.quote && (m = at(R.quoteLine, src, i))) {
        push({ t: "quote", c: [...mark("> "), ...sourceInto(m[1]!, { ...st, quote: true })] });
        buf += m[0].slice(2 + m[1]!.length); // the line break
        i += m[0].length;
        continue;
      }
      if ((m = at(R.heading, src, i))) {
        const lead = /^#{1,3} +/.exec(m[0])![0];
        push({ t: "heading", level: m[1]!.length as 1 | 2 | 3, c: [...mark(lead), ...inner(m[2]!)] });
        buf += m[0].slice(lead.length + m[2]!.length);
        i += m[0].length;
        continue;
      }
      if ((m = at(R.subtext, src, i))) {
        const lead = /^-# +/.exec(m[0])![0];
        push({ t: "subtext", c: [...mark(lead), ...inner(m[1]!)] });
        buf += m[0].slice(lead.length + m[1]!.length);
        i += m[0].length;
        continue;
      }
      if ((m = at(R.listItem, src, i))) {
        const bullet = /^(?:[-*]|\d{1,9}\.) +/.exec(m[0].slice(m[1]!.length))![0];
        buf += m[1];
        push({ t: "li", c: [...mark(bullet), ...inner(m[3]!)] });
        buf += m[0].slice(m[1]!.length + bullet.length + m[3]!.length);
        i += m[0].length;
        continue;
      }
    }

    if (!TRIGGER.has(ch)) {
      buf += ch;
      i++;
      continue;
    }
    if ((m = at(R.escape, src, i))) {
      push({ t: "mark", v: "\\" });
      buf += m[1];
    } else if ((m = at(R.codeblock, src, i))) {
      push({ t: "codeblock", c: [...mark("```"), ...text(m[0].slice(3, -3)), ...mark("```")] });
    } else if ((m = at(R.code, src, i))) {
      push({ t: "code", c: [...mark(m[1]!), ...text(m[2]!), ...mark(m[1]!)] });
    } else if ((m = at(R.time, src, i))) {
      push({ t: "time", c: text(m[0]) });
    } else if ((m = at(R.emoji, src, i)) || (m = at(R.angleLink, src, i)) || (m = at(R.token, src, i)) || (m = at(R.url, src, i))) {
      buf += m[0];
    } else if ((m = at(R.spoiler, src, i))) {
      push({ t: "spoiler", c: [...mark("||"), ...inner(m[1]!), ...mark("||")] });
    } else if ((m = at(R.strong, src, i))) {
      push({ t: "strong", c: [...mark("**"), ...inner(m[1]!), ...mark("**")] });
    } else if ((m = at(R.underline, src, i))) {
      push({ t: "u", c: [...mark("__"), ...inner(m[1]!), ...mark("__")] });
    } else if ((m = at(R.emStar, src, i)) || (m = at(R.emUnder, src, i))) {
      push({ t: "em", c: [...mark(ch), ...inner(m[1]!), ...mark(ch)] });
    } else if ((m = at(R.strike, src, i))) {
      push({ t: "s", c: [...mark("~~"), ...inner(m[1]!), ...mark("~~")] });
    } else {
      buf += ch;
      i++;
      continue;
    }
    i += m[0].length;
  }
  flush();
  return out;
}

/** The message as typed, for drawing formatting in the composer. Joining the leaves gives back `src` exactly. */
export function parseSource(src: string): SrcNode[] {
  return sourceInto(src, { inline: false, quote: false, open: false });
}

// ---- Code, for the encoders ----

/**
 * The text split into code and not-code, in order. @mentions and #channels are only encoded outside code, so
 * "`@everyone`" in a code span stays text and pings nobody (as in Discord).
 */
export function splitCode(src: string): { code: boolean; s: string }[] {
  const out: { code: boolean; s: string }[] = [];
  let last = 0;
  let i = 0;
  while (i < src.length) {
    const ch = src[i];
    if (ch === "\\") {
      i += 2;
      continue;
    }
    if (ch !== "`") {
      i++;
      continue;
    }
    const m = at(R.codeblock, src, i) ?? at(R.code, src, i);
    if (!m) {
      i++;
      continue;
    }
    if (i > last) out.push({ code: false, s: src.slice(last, i) });
    out.push({ code: true, s: m[0] });
    i += m[0].length;
    last = i;
  }
  if (last < src.length) out.push({ code: false, s: src.slice(last) });
  return out;
}

/** Applies `fn` to the parts of the text outside code. */
export function outsideCode(src: string, fn: (s: string) => string): string {
  if (!src.includes("`")) return fn(src);
  return splitCode(src)
    .map((p) => (p.code ? p.s : fn(p.s)))
    .join("");
}

/** The text with code taken out (for counting mentions: a token inside code mentions nobody). */
export const withoutCode = (src: string) => (src.includes("`") ? splitCode(src).map((p) => (p.code ? " " : p.s)).join("") : src);

// ---- Plain text ----

export interface PlainOptions {
  /** What a spoiler becomes (default: its text, unhidden). Notifications and reply excerpts hide it. */
  spoiler?: string;
  /** Formats a <t:…> timestamp (default: an ISO-like UTC time). */
  time?: (ts: number, style: TimeStyle) => string;
}

export const SPOILER_MASK = "▒▒▒▒▒";

const defaultTime = (ts: number) => `${new Date(ts).toISOString().slice(0, 16).replace("T", " ")} UTC`;

/** Message text without formatting marks: for notifications, reply excerpts, the thread header. Tokens stay as they are. */
export function markdownToPlain(src: string, opts: PlainOptions = {}): string {
  if (!hasMarkdown(src)) return src;
  const walk = (nodes: MdNode[]): string =>
    nodes
      .map((n): string => {
        switch (n.t) {
          case "text":
          case "code":
            return n.v;
          case "codeblock":
            return `\n${n.v}\n`;
          case "spoiler":
            return opts.spoiler ?? walk(n.c);
          case "time":
            return (opts.time ?? defaultTime)(n.ts, n.style);
          case "emoji":
            return `:${n.name}:`;
          case "list":
            return `\n${n.items.map((it, k) => `${n.ordered ? `${n.start + k}.` : "•"} ${walk(it).trim()}`).join("\n")}\n`;
          case "quote":
          case "heading":
          case "subtext":
            return `\n${walk(n.c)}\n`;
          default:
            return walk(n.c);
        }
      })
      .join("");
  return walk(parseMarkdown(src)).replace(/\n{3,}/g, "\n\n").trim();
}

// ---- Link previews ----

/**
 * Links that should get a preview, in order, without repeats: bare links, but not links in code
 * (they're text there) or in spoilers (a preview would give the spoiler away). A link wrapped in <…> gets none.
 */
export function previewLinks(text: string, max: number): string[] {
  const out: string[] = [];
  const add = (href: string) => {
    let h: string;
    try {
      h = new URL(href).href;
    } catch {
      return;
    }
    if (out.length < max && !out.includes(h)) out.push(h);
  };
  const walk = (nodes: MdNode[]) => {
    for (const n of nodes) {
      if (out.length >= max) return;
      if (n.t === "text") {
        for (const p of splitLinks(n.v)) if (p.kind === "link" && !p.quiet) add(p.href);
      } else if (n.t === "list") n.items.forEach(walk);
      else if (n.t !== "spoiler" && "c" in n) walk(n.c);
    }
  };
  walk(parseMarkdown(text));
  return out;
}
